import { action, computed, observable, makeAutoObservable } from 'mobx';
import { FormInputState, FormModel, ResultValue, group, Result, ResultError } from 'src_common/common/mobx-utils/Form';
import { InputStatus } from 'src/components/Input/InputStatus';
import { validateRequire, validateExpectNumber } from 'src/api/utils/formValidators';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { apiCommon } from 'src/api/ApiCommon';
import { QuickLinkTrayModelType } from 'src/api/config/quick_link_tray/getQuickLinkTray';
import { ParamsType } from 'src/api_openapi/generated/openapi_website_cms_createQuickLinkTray';
import { UploadImageState } from 'src/AppState/UploadImageState/UploadImageState';
import { SdkStaff, SportsListType } from 'src/AppState/sdk/staff';

type QuickLinkTrayBodyType = ParamsType['body'];

export interface QuickLinkTrayType {
    sport: string;
    label: string;
    is_active: boolean;
    redirect_option: string;
    direct_url: string;
    event_id: number | null;
    competition_id: number | null;
    casino_game_id: number | null;
}

interface QuickLinkTrayLoading {
    type: 'loading';
}

interface QuickLinkTrayReady {
    type: 'ready';
    value: Array<QuickLinkTrayModelType>;
}

interface QuickLinkTrayError {
    type: 'error';
    value: string;
}

type QuickLinkTrayContentResponse = QuickLinkTrayLoading | QuickLinkTrayReady | QuickLinkTrayError;

interface SportListOptionType {
    key: string;
    name: string;
}

export interface CompetitionSportType {
    competitionId: number | null;
    sport: string;
}

export class QuickLinkTrayState {
    @observable private readonly quickLinkTrayListResource: Resource<Array<QuickLinkTrayModelType>>;
    @observable private readonly allSports: Resource<SportsListType>;
    @observable public isEditMode: boolean = false;
    @observable private inEditQuickLinkTray: QuickLinkTrayModelType | null = null;
    @observable public errorMessageForList: string | null = null;

    public readonly sportModel: FormModel<string>;
    public readonly sport: FormInputState<string>;
    public readonly sportStatus: InputStatus;
    public readonly labelModel: FormModel<string>;
    public readonly label: FormInputState<string>;
    public readonly labelStatus: InputStatus;
    public readonly isActiveModel: FormModel<boolean>;
    public readonly isActive: FormInputState<boolean>;
    public readonly isActiveStatus: InputStatus;
    public readonly redirectOptionModel: FormModel<string>;
    public readonly redirectOption: FormInputState<string>;
    public readonly redirectOptionStatus: InputStatus;
    public readonly directUrlModel: FormModel<string>;
    public readonly directUrl: FormInputState<string>;
    public readonly directUrlStatus: InputStatus;
    public readonly eventIdModel: FormModel<number | null>;
    public readonly eventId: FormInputState<string>;
    public readonly eventIdStatus: InputStatus;
    public readonly competitionIdModel: FormModel<number | null>;
    public readonly competitionId: FormInputState<string>;
    public readonly competitionIdStatus: InputStatus;
    public readonly casinoGameIdModel: FormModel<number | null>;
    public readonly casinoGameId: FormInputState<string>;
    public readonly casinoGameIdStatus: InputStatus;

    private readonly form: FormModel<QuickLinkTrayType>;
    public readonly competitionSport: FormModel<CompetitionSportType>;

    public uploadIcon: UploadImageState;

    constructor(private readonly sdkStaff: SdkStaff) {
        this.quickLinkTrayListResource = new Resource(async (): Promise<Array<QuickLinkTrayModelType>> => {
            return await apiCommon.getQuickLinkTray.run({});
        });

        this.allSports = new Resource(async (): Promise<SportsListType> => {
            return await this.sdkStaff.getAllSports();
        });

        this.sport = new FormInputState('', false);
        this.sportModel = this.sport.toModel();
        this.sportStatus = new InputStatus(this.sportModel);

        this.label = new FormInputState('', false);
        this.labelModel = this.label.map(validateRequire);
        this.labelStatus = new InputStatus(this.labelModel);

        this.isActive = new FormInputState(false, false);
        this.isActiveModel = this.isActive.toModel();
        this.isActiveStatus = new InputStatus(this.isActiveModel);

        this.redirectOption = new FormInputState('sport', false);
        this.redirectOptionModel = this.redirectOption.map(validateRequire);
        this.redirectOptionStatus = new InputStatus(this.redirectOptionModel);

        this.directUrl = new FormInputState('', false);
        this.directUrlModel = this.directUrl.toModel();
        this.directUrlStatus = new InputStatus(this.directUrlModel);

        this.eventId = new FormInputState('', false);
        this.eventIdModel = this.eventId.map(validateExpectNumber);
        this.eventIdStatus = new InputStatus(this.eventIdModel);

        this.competitionId = new FormInputState('', false);
        this.competitionIdModel = this.competitionId.map(validateExpectNumber);
        this.competitionIdStatus = new InputStatus(this.competitionIdModel);

        this.casinoGameId = new FormInputState('', false);
        this.casinoGameIdModel = this.casinoGameId.map(validateExpectNumber);
        this.casinoGameIdStatus = new InputStatus(this.casinoGameIdModel);

        this.form = group({
            sport: this.sportModel,
            label: this.labelModel,
            is_active: this.isActiveModel,
            redirect_option: this.redirectOptionModel,
            direct_url: this.directUrlModel,
            event_id: this.eventIdModel,
            competition_id: this.competitionIdModel,
            casino_game_id: this.casinoGameIdModel,
        }).map((item: QuickLinkTrayType): Result<QuickLinkTrayType> => {
            return new ResultValue({
                sport: item.sport,
                label: item.label,
                is_active: item.is_active,
                redirect_option: item.redirect_option,
                direct_url: item.direct_url,
                event_id: item.event_id,
                competition_id: item.competition_id,
                casino_game_id: item.casino_game_id,
            });
        });

        this.competitionSport = group({
            competitionId: this.competitionIdModel,
            sport: this.sportModel,
        }).map((item: CompetitionSportType): Result<CompetitionSportType> => {
            if (item.competitionId === null || item.sport === '') {
                return new ResultError('Both values needed');
            }

            return new ResultValue({
                competitionId: item.competitionId,
                sport: item.sport,
            });
        });

        this.uploadIcon = new UploadImageState('quick-link-tray/icon');
        makeAutoObservable(this);
    }

    @action public resetForm = (): void => {
        this.form.reset();
        this.isEditMode = false;
        this.inEditQuickLinkTray = null;
        this.uploadIcon.handleResetImage();
    };

    @action private handleCreateApiCall = async (quickLinkTray: QuickLinkTrayBodyType): Promise<number | null> => {
        try {
            return await apiCommon.postCreateQuickLinkTray.run({ data: quickLinkTray });
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    @action public handleCreate = async (): Promise<void> => {
        this.form.setAsVisited();
        const result = this.form.result;
        if (result instanceof ResultValue) {
            const topElem = this.quickLinkTraySorted[0] ?? null;
            const display_order =
                this.isEditMode && this.inEditQuickLinkTray !== null
                    ? this.inEditQuickLinkTray.display_order
                    : topElem !== null
                      ? topElem.display_order + 1
                      : 1;

            const body = {
                ...result.value,
                icon: this.uploadIcon.imgUrl,
                display_order: display_order,
            };

            const submitResult = await this.handleCreateApiCall(body);

            if (submitResult !== null) {
                await this.quickLinkTrayListResource.refresh();
                this.resetForm();
            } else {
                console.log('Error');
                this.handleError('Something went wrong.');
            }
        }
    };

    @action public handleReset = (): void => {
        if (this.isEditMode && this.inEditQuickLinkTray !== null) {
            this.setEditQuickLinkTray(this.inEditQuickLinkTray);
        } else {
            this.resetForm();
        }
    };

    @action public setEditQuickLinkTray = async (quickLinkTray: QuickLinkTrayModelType): Promise<void> => {
        const event_id = quickLinkTray.event_id ?? '';
        const competition_id = quickLinkTray.competition_id ?? '';
        const casino_game_id = quickLinkTray.casino_game_id ?? '';
        const direct_url = quickLinkTray.direct_url ?? '';
        this.inEditQuickLinkTray = quickLinkTray;
        this.sport.setValue(quickLinkTray.sport ?? '');
        this.label.setValue(quickLinkTray.label ?? '');
        this.redirectOption.setValue(quickLinkTray.redirect_option ?? '');
        this.eventId.setValue(event_id.toString());
        this.directUrl.setValue(direct_url);
        this.competitionId.setValue(competition_id.toString());
        this.casinoGameId.setValue(casino_game_id.toString());
        this.isActive.setValue(quickLinkTray.is_active ?? '');
        this.uploadIcon.handleChooseImage(quickLinkTray.icon ?? '');
    };

    @action public handleEdit = async (): Promise<void> => {
        this.form.setAsVisited();
        const result = this.form.result;

        if (result instanceof ResultValue && this.inEditQuickLinkTray !== null) {
            const payload = {
                ...result.value,
                icon: this.uploadIcon.imgUrl,
                display_order: this.inEditQuickLinkTray.display_order,
            };

            const submitResult = await this.handleEditApiCall(payload, this.inEditQuickLinkTray.id);

            if (submitResult !== null) {
                await this.quickLinkTrayListResource.refresh();
                this.resetForm();
            } else {
                console.log('Error');
            }
        }
    };

    @action private handleEditApiCall = async (
        quickLinkTray: QuickLinkTrayBodyType,
        id: number
    ): Promise<number | null> => {
        try {
            return await apiCommon.putUpdateQuickLinkTray.run({ data: quickLinkTray, id });
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    @computed private get quickLinkTrayListResponse(): QuickLinkTrayContentResponse {
        const quickLinkTray = this.quickLinkTrayListResource.get();
        if (quickLinkTray.type === 'ready' && quickLinkTray.value !== null) {
            return {
                type: 'ready',
                value: quickLinkTray.value,
            };
        }
        if (quickLinkTray.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching quick link tray',
        };
    }

    @computed public get quickLinkTrayList(): Array<QuickLinkTrayModelType> {
        if (this.quickLinkTrayListResponse.type === 'ready') {
            return this.quickLinkTrayListResponse.value;
        }
        return [];
    }

    @computed public get isLoading(): boolean {
        return this.quickLinkTrayListResponse.type === 'loading';
    }

    @action public setEditMode = (value: boolean): void => {
        this.isEditMode = value;
    };

    @action public setDefaultLabel = (value: string): void => {
        const sportId = value;
        const find = this.sportList.find((elem) => elem.id === sportId) ?? null;
        if (find !== null) {
            this.label.setValue(find.name);
        }
    };

    @action public resetLabel = (): void => {
        this.label.setValue('');
    };

    @action public handleRemoveApiCall = async (id: number): Promise<void> => {
        const removeResult = await apiCommon.deleteQuickLinkTray.run({ id });

        if (removeResult) {
            await this.quickLinkTrayListResource.refresh();
        } else {
            console.log('Error');
        }
    };

    @action public setActiveFlag = (quickLinkTray: QuickLinkTrayModelType): void => {
        const payload: QuickLinkTrayModelType = {
            ...quickLinkTray,
            is_active: !quickLinkTray.is_active,
        };

        this.handleEditActiveFlags(payload);
    };

    @action private handleEditActiveFlags = async (payload: QuickLinkTrayModelType): Promise<void> => {
        const submitResult = await this.handleEditApiCall(payload, payload.id);

        if (submitResult !== null) {
            await this.quickLinkTrayListResource.refresh();
            this.resetForm();
        } else {
            console.log('Error');
        }
    };

    @computed public get quickLinkTraySorted(): Array<QuickLinkTrayModelType> {
        return this.quickLinkTrayList.sort((quickLink1: QuickLinkTrayModelType, quickLink2: QuickLinkTrayModelType) => {
            if (quickLink1 !== null && quickLink2 !== null) {
                if (quickLink1.display_order > quickLink2.display_order) {
                    return -1;
                }
                if (quickLink1.display_order < quickLink2.display_order) {
                    return 1;
                }
                return 0;
            }
            return -1;
        });
    }

    @computed private get sportList(): SportsListType {
        const allSports = this.allSports.get();
        if (allSports.type === 'ready') {
            return allSports.value;
        }
        return [];
    }

    @computed public get sportListOptions(): Array<SportListOptionType> {
        const temp = this.sportList.map((elem) => {
            return {
                key: elem.id,
                name: elem.name,
            };
        });

        return [
            {
                key: '',
                name: 'Select sport',
            },
            ...temp,
        ];
    }

    public get redirectOptions(): Array<SportListOptionType> {
        return [
            {
                key: 'direct-link',
                name: 'Direct link',
            },
            {
                key: 'event-id',
                name: 'Event id',
            },
            {
                key: 'sport',
                name: 'Sport',
            },
            {
                key: 'competition-id',
                name: 'Competition id',
            },
            {
                key: 'casino-game-id',
                name: 'Casino game id',
            },
        ];
    }

    moveItemUp = async (id: number): Promise<void> => {
        const data = {
            id,
            direction: 'up',
        };
        try {
            const result = await apiCommon.postOrderQuickLinkTray.run({ data });

            if (result) {
                await this.quickLinkTrayListResource.refresh();
                this.resetForm();
            } else {
                this.handleError('Can not move item up.');
            }
        } catch (error) {
            this.handleError('Can not move item up.');
        }
    };

    moveItemDown = async (id: number): Promise<void> => {
        const data = {
            id,
            direction: 'down',
        };

        try {
            const result = await apiCommon.postOrderQuickLinkTray.run({ data });

            if (result) {
                await this.quickLinkTrayListResource.refresh();
                this.resetForm();
            } else {
                this.handleError('Can not move item down.');
            }
        } catch (error) {
            this.handleError('Can not move item down.');
        }
    };

    @action handleError = async (message: string): Promise<void> => {
        this.errorMessageForList = message;
        await this.wait(3000);
        this.errorMessageForList = null;
    };

    wait = async (time: number): Promise<void> => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, time);
        });
    };
}
